import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Package, Zap } from 'lucide-react';
import { t } from '@/i18n';

const MATERIALS = ['Limestone GCC', 'Limestone PCC', 'By-product'];

export const AddPlanningDialog = ({ open, onOpenChange, lineName, dayName, orders = [], materialColors = {}, onAdd }) => {
  const [material, setMaterial] = useState('Limestone GCC');
  const [orderId, setOrderId] = useState('');
  const [energyTariff, setEnergyTariff] = useState('offpeak');

  const pendingOrders = orders.filter(o => ['pending', 'confirmed'].includes(o.status));
  const firstMaterial = pendingOrders.find(o => o.items?.[0]?.material)?.items?.[0]?.material;

  const handleAdd = () => {
    const order = pendingOrders.find(o => o.id === orderId);
    const grade = order?.items?.[0]?.grade || 'Omyacarb 2-AV';
    const plannedOutput = order?.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 500;

    onAdd?.({
      day: dayName,
      material: material || firstMaterial || MATERIALS[0],
      grade,
      orderId: orderId || null,
      utilization: 75,
      energyTariff,
      shift: 'morning',
      plannedOutput,
      actualOutput: null,
    });
    setMaterial('Limestone GCC');
    setOrderId('');
    setEnergyTariff('offpeak');
    onOpenChange(false);
  };

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setMaterial('Limestone GCC');
      setOrderId('');
      setEnergyTariff('offpeak');
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-white/95 backdrop-blur-md">
        <DialogHeader>
          <DialogTitle>{t('addPlanning.title')} — {lineName} · {dayName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              {t('addPlanning.material')}
            </Label>
            <Select value={material} onValueChange={setMaterial}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MATERIALS.map(mat => (
                  <SelectItem key={mat} value={mat}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: materialColors[mat] || '#94a3b8' }} />
                      {mat}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('addPlanning.order')}</Label>
            <Select value={orderId || 'none'} onValueChange={v => setOrderId(v === 'none' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder={t('addPlanning.selectOrder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('addPlanning.noOrder')}</SelectItem>
                {pendingOrders.map(o => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.id} — {o.customer?.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              {t('addPlanning.energyTariff')}
            </Label>
            <Select value={energyTariff} onValueChange={setEnergyTariff}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="offpeak">{t('planning.off')} {t('addPlanning.tariff')}</SelectItem>
                <SelectItem value="peak">{t('planning.peak')} {t('addPlanning.tariff')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => handleClose(false)}>
            {t('addPlanning.cancel')}
          </Button>
          <Button onClick={handleAdd}>
            {t('addPlanning.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
